import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { KeyRound, ShieldCheck } from 'lucide-react';
import type { Role } from '@rundown/policy';
import { Button, Modal, Pill } from '../../../../components';
import { formatDate } from '../../../../lib/format';
import { accountQueryOptions } from '../../api';
import {
  useRestoreAccount,
  useSetAccountPassword,
  useSuspendAccount,
  useUpdateMembership,
} from '../../useSettingsMutations';
import { hasBilling, type Account, type Grade, type Membership } from '../../types';
import {
  Avatar,
  CredentialsCard,
  ErrorText,
  INPUT_CLASS,
  MustChangeToggle,
  PasswordInput,
  DeliveryFields,
  RolePicker,
  isClientRole,
  StatusPill,
  generatePassword,
  passwordMeetsPolicy,
} from './shared';

function Section({ title, hint, children }: { title: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3 border-b border-line py-4 first:pt-0 last:border-b-0 last:pb-0">
      <div>
        <h3 className="fs-13 font-semibold text-ink">{title}</h3>
        {hint !== undefined && <p className="mt-0.5 fs-12 text-muted">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

// --- Role ----------------------------------------------------------------------

function RoleSection({
  workspaceId,
  membership,
  isSelf,
}: {
  workspaceId: string;
  membership: Membership;
  isSelf: boolean;
}) {
  const update = useUpdateMembership(workspaceId);

  return (
    <Section
      title="Role"
      hint={
        isSelf
          ? "You can't change your own role — ask another admin."
          : 'Decides what they can see and do here. Saves as soon as you pick.'
      }
    >
      <RolePicker
        name={`role-${membership.id}`}
        value={membership.role}
        disabled={isSelf || update.isPending}
        onChange={(role: Role) => update.mutate({ membershipId: membership.id, patch: { role } })}
      />
      {update.isPending && <p className="fs-12 text-muted">Saving…</p>}
      <ErrorText message={update.error?.message} />
    </Section>
  );
}

// --- Delivery ------------------------------------------------------------------

/**
 * Grade, factor and this client's share of their week.
 *
 * Shown only for a Noorix role — there is nothing to set for someone who asks
 * for work rather than doing it — and hidden entirely from a caller without
 * `billing.read`, whose payload does not carry the grade at all (see
 * `hasBilling`). That is the same read-side gate the server enforces on the
 * write, so a Developer is not shown a form the API would refuse.
 *
 * Saved on an explicit button rather than on change, unlike Role above: a
 * grade and a factor move together, and committing each keystroke of a
 * basis-point field would write a trail entry per digit.
 */
function DeliverySection({ workspaceId, membership }: { workspaceId: string; membership: Membership }) {
  const update = useUpdateMembership(workspaceId);
  const billing = hasBilling(membership) ? membership : null;

  const [grade, setGrade] = useState<Grade | null>(billing?.grade ?? null);
  const [billingFactorBp, setBillingFactorBp] = useState<number | null>(billing?.billingFactorBp ?? null);
  const [allocatedHours, setAllocatedHours] = useState(membership.allocatedHours);

  if (isClientRole(membership.role) || billing === null) return null;

  const changed =
    grade !== billing.grade ||
    billingFactorBp !== billing.billingFactorBp ||
    allocatedHours !== membership.allocatedHours;

  return (
    <Section title="Delivery" hint="Hours can only be logged against someone who has a grade.">
      <DeliveryFields
        grade={grade}
        billingFactorBp={billingFactorBp}
        allocatedHours={allocatedHours}
        onGrade={setGrade}
        onFactor={setBillingFactorBp}
        onAllocated={setAllocatedHours}
        disabled={update.isPending}
      />
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="primary"
          disabled={!changed || update.isPending}
          onClick={() =>
            update.mutate({
              membershipId: membership.id,
              patch: { grade, billingFactorBp, allocatedHours },
            })
          }
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
      <ErrorText message={update.error?.message} />
    </Section>
  );
}

// --- Sign-in -------------------------------------------------------------------

function signInSummary(account: Account): { icon: ReactNode; text: string } {
  switch (account.signIn) {
    case 'password':
      return { icon: <KeyRound size={15} />, text: 'Signs in with email and password' };
    case 'google':
      return { icon: <ShieldCheck size={15} />, text: 'Signs in with Google' };
    case 'none':
      return {
        icon: <KeyRound size={15} />,
        text: "No way to sign in yet — they haven't used Google and have no password",
      };
  }
}

function SignInSection({
  workspaceId,
  membership,
  isSelf,
}: {
  workspaceId: string;
  membership: Membership;
  isSelf: boolean;
}) {
  const query = useQuery(accountQueryOptions(workspaceId, membership.id));
  const setPassword = useSetAccountPassword(workspaceId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [mustChange, setMustChange] = useState(true);
  const [handedOver, setHandedOver] = useState<string | null>(null);

  if (query.isPending) {
    return (
      <Section title="Sign-in">
        <p className="fs-12 text-muted">Loading…</p>
      </Section>
    );
  }
  if (query.error !== null) {
    return (
      <Section title="Sign-in">
        <ErrorText message={query.error.message} />
      </Section>
    );
  }

  const account = query.data;
  const summary = signInSummary(account);
  const hasPassword = account.signIn === 'password';

  const startEditing = () => {
    setDraft(generatePassword());
    setMustChange(true);
    setHandedOver(null);
    setPassword.reset();
    setEditing(true);
  };

  const save = () => {
    if (!passwordMeetsPolicy(draft)) return;
    const password = draft;
    setPassword.mutate(
      { membershipId: membership.id, input: { password, mustChange } },
      {
        onSuccess: () => {
          setHandedOver(password);
          setEditing(false);
          setDraft('');
        },
      },
    );
  };

  return (
    <Section title="Sign-in">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 fs-13 text-ink">
          <span className="text-muted">{summary.icon}</span>
          {summary.text}
        </span>
        {account.mfaEnabled && <Pill tone="accent">2FA on</Pill>}
      </div>

      {isSelf ? (
        <p className="fs-12 text-muted">To change your own password, go to Account → Security.</p>
      ) : account.signIn === 'google' ? (
        <p className="fs-12 text-muted">Google manages their password, so there's nothing to set here.</p>
      ) : handedOver !== null ? (
        <>
          <CredentialsCard email={membership.email} password={handedOver} />
          <div>
            <Button variant="ghost" size="sm" onClick={() => setHandedOver(null)}>
              I've sent it
            </Button>
          </div>
        </>
      ) : editing ? (
        <div className="flex flex-col gap-3 rounded-lg bg-surface p-3">
          <PasswordInput value={draft} onChange={setDraft} autoFocus />
          <MustChangeToggle checked={mustChange} onChange={setMustChange} />
          {/* Says what this actually does, not what it looks like it does.
              "Signed out everywhere" is true and reads as tidy-up; the part an
              admin needs before they click is that the password they are about
              to type is one they will know, for an account that may hold
              memberships of clients they cannot see. AccountsService.setPassword
              carries the same note for the server side. */}
          <p className="fs-11.5 text-muted">
            {hasPassword
              ? 'Their current password stops working and they are signed out everywhere — in every workspace, not just this one. Until they change it, this password is one you both know. They are emailed that an admin changed it.'
              : 'This password works in every workspace they belong to, not just this one, and it is one you both know until they change it. They are emailed that an admin set it.'}
          </p>
          <ErrorText message={setPassword.error?.message} />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!passwordMeetsPolicy(draft) || setPassword.isPending}
              onClick={save}
            >
              {setPassword.isPending ? 'Saving…' : hasPassword ? 'Reset password' : 'Set password'}
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <Button variant={hasPassword ? 'secondary' : 'primary'} size="sm" onClick={startEditing}>
            <KeyRound size={14} />
            {hasPassword ? 'Reset password' : 'Set a password'}
          </Button>
        </div>
      )}
    </Section>
  );
}

// --- Access --------------------------------------------------------------------

function WorkspaceAccess({
  workspaceId,
  membership,
  isSelf,
}: {
  workspaceId: string;
  membership: Membership;
  isSelf: boolean;
}) {
  const update = useUpdateMembership(workspaceId);
  const [confirming, setConfirming] = useState(false);

  const toggle = () =>
    update.mutate(
      { membershipId: membership.id, patch: { active: !membership.active } },
      { onSuccess: () => setConfirming(false) },
    );

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 basis-[13.75rem]">
          <p className="fs-13 font-medium text-ink">
            {membership.active ? 'Remove from this workspace' : 'Removed from this workspace'}
          </p>
          <p className="fs-12 text-muted">
            {membership.active
              ? "They lose access to this workspace only. Their account and other workspaces aren't affected."
              : 'Give them their seat back with the same role.'}
          </p>
        </div>
        {!confirming && (
          <Button
            variant="secondary"
            size="sm"
            disabled={isSelf || update.isPending}
            title={isSelf ? "You can't remove yourself." : undefined}
            onClick={() => (membership.active ? setConfirming(true) : toggle())}
          >
            {membership.active ? 'Remove' : update.isPending ? 'Restoring…' : 'Restore access'}
          </Button>
        )}
      </div>
      {confirming && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="mr-auto fs-12 text-ink-soft">Remove {membership.name}?</span>
          <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" disabled={update.isPending} onClick={toggle}>
            {update.isPending ? 'Removing…' : 'Yes, remove'}
          </Button>
        </div>
      )}
      <ErrorText message={update.error?.message} />
    </div>
  );
}

/**
 * Suspension reaches every workspace the person belongs to, so it sits apart
 * from the per-workspace control above, asks for a reason, and says so.
 */
function AccountAccess({
  workspaceId,
  membership,
  isSelf,
}: {
  workspaceId: string;
  membership: Membership;
  isSelf: boolean;
}) {
  const query = useQuery(accountQueryOptions(workspaceId, membership.id));
  const suspend = useSuspendAccount(workspaceId);
  const restore = useRestoreAccount(workspaceId);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');

  if (query.data === undefined) return null;
  const account = query.data;

  // Either switch off means they cannot get in: an admin suspension, or nauth
  // locking the account after repeated failed sign-ins. Both need a way back.
  const lockedOut = !account.active || account.suspended;

  if (lockedOut) {
    return (
      <div className="flex flex-col gap-2 rounded-lg border border-danger-border bg-danger-tint/40 p-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1 basis-[13.75rem]">
            <p className="fs-13 font-medium text-ink">
              {account.active ? 'Locked after too many failed sign-ins' : 'Account suspended'}
            </p>
            <p className="fs-12 text-muted">
              They can't sign in to any workspace. Restoring keeps their existing password.
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            disabled={restore.isPending}
            onClick={() => restore.mutate(membership.id)}
          >
            {restore.isPending ? 'Restoring…' : account.active ? 'Unlock' : 'Unsuspend'}
          </Button>
        </div>
        <ErrorText message={restore.error?.message} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-danger-border p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 basis-[13.75rem]">
          <p className="fs-13 font-medium text-danger">Suspend account</p>
          <p className="fs-12 text-muted">
            Signs them out and blocks sign-in to <strong className="text-ink-soft">every</strong> workspace,
            not just this one. You can undo this.
          </p>
        </div>
        {!confirming && (
          <Button
            variant="danger"
            size="sm"
            disabled={isSelf}
            title={isSelf ? "You can't suspend yourself." : undefined}
            onClick={() => setConfirming(true)}
          >
            Suspend
          </Button>
        )}
      </div>
      {confirming && (
        <div className="flex flex-col gap-2">
          <input
            autoFocus
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Reason (required) — e.g. Left the company"
            aria-label="Reason for suspending"
            className={INPUT_CLASS}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setConfirming(false);
                setReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={reason.trim() === '' || suspend.isPending}
              onClick={() =>
                suspend.mutate(
                  { membershipId: membership.id, reason: reason.trim() },
                  {
                    onSuccess: () => {
                      setConfirming(false);
                      setReason('');
                    },
                  },
                )
              }
            >
              {suspend.isPending ? 'Suspending…' : 'Suspend account'}
            </Button>
          </div>
        </div>
      )}
      <ErrorText message={suspend.error?.message} />
    </div>
  );
}

// --- Dialog --------------------------------------------------------------------

export function ManagePersonDialog({
  workspaceId,
  membership,
  isSelf,
  onClose,
}: {
  workspaceId: string;
  membership: Membership;
  isSelf: boolean;
  onClose: () => void;
}) {
  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      className="max-w-[37.5rem]"
      title={
        <span className="flex items-center gap-3">
          <Avatar name={membership.name} size={36} />
          <span className="min-w-0">
            <span className="flex flex-wrap items-center gap-2">
              <span className="truncate">{membership.name}</span>
              {isSelf && <Pill tone="accent">You</Pill>}
              <StatusPill membership={membership} />
            </span>
            <span className="block truncate fs-12 font-normal text-muted">
              {membership.email} · added {formatDate(membership.createdAt)}
            </span>
          </span>
        </span>
      }
      footer={
        <Button variant="secondary" size="sm" onClick={onClose}>
          Close
        </Button>
      }
    >
      <RoleSection workspaceId={workspaceId} membership={membership} isSelf={isSelf} />
      <DeliverySection workspaceId={workspaceId} membership={membership} />
      <SignInSection workspaceId={workspaceId} membership={membership} isSelf={isSelf} />
      <Section title="Access">
        <WorkspaceAccess workspaceId={workspaceId} membership={membership} isSelf={isSelf} />
        <AccountAccess workspaceId={workspaceId} membership={membership} isSelf={isSelf} />
      </Section>
    </Modal>
  );
}
